import { useEffect, useState } from 'react';
// @mui
import { Stack, Alert, styled, Card, Typography, Box, InputBase, InputLabel, Grid, Button } from '@mui/material';
// hooks
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { LoadCanvasTemplateNoReload, loadCaptchaEnginge, validateCaptcha } from 'react-simple-captcha';
import { LoadingButton } from '@mui/lab';
import useLocales from '../../hooks/useLocales';
import { getOtpErrorMessage } from '../../utils/formatString';
import palette from '../../theme/palette';
import { checkIfNumberExist } from '../../utils/formatObject';
import uuidv4 from '../../utils/uuidv4';
import conObj from '../../utils/connection-assistant';
import { validatePhoneNumber } from '../../utils/validatePhoneNumber';
import MyDialog from '../../components/dialog-component/my-dialog';
import { openReset } from '../../redux/slices/authUiSlice';

// ----------------------------------------------------------------------

const FormContainer = styled('form')({
  padding: '16px',
  maxWidth: '900px',
  margin: '16px auto',
});

const StyledInput = styled(InputBase)({
  opacity: 0.9,
  fontWeight: 400,
  fontFamily: 'sans-serif',
  flexGrow: 1,
  backgroundColor: '#d3d3d3',
  color: '#000',
  borderRadius: '10px',
  padding: '8.5px 13px',
  input: {
    '&::placeholder': {
      opacity: 0.9,
      color: palette.visitor.input.placeholder,
    },
  },
});

const SubmitButton = styled(LoadingButton)({
  width: '100%',
  marginBottom: 8,
  border: '1px solid #ffce30',
  backgroundColor: '#d4af37',
  color: '#fff',
  fontWeight: 500,
  '&:disabled': {
    filter: 'grayscale(1)',
  },
});

const StyledLabel = styled(InputLabel)({
  opacity: 0.9,
  fontSize: '15px',
  color: '#fff',
});

const ErrorText = styled(Typography)({
  opacity: 0.9,
  fontSize: '.75rem',
  color: palette.visitor.errorText,
});

// ----------------------------------------------------------------------

export default function ForgotPassword({ handleOpen, handleClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const [error, setError] = useState('');
  const [formError, setFormError] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    userPhoneNumber: '',
    captcha: '',
  });

  /**
   * 🔥 Load captcha ONLY when dialog opens
   */
  useEffect(() => {
    if (handleOpen) {
      setTimeout(() => {
        loadCaptchaEnginge(4);
      }, 100);
    }
  }, [handleOpen]);

  const handleFormChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const isValid = () => {
    const _errors = {};
    let valid = true;
    const phoneNumber = formData?.userPhoneNumber?.trim();

    // Phone validation
    if (!phoneNumber) {
      valid = false;
      _errors.userPhoneNumber = translate('invalid_x', {
        x: translate('phone_number'),
      });
    } else if (!/^[3-9]\d{8}$/.test(phoneNumber)) {
      valid = false;
      _errors.userPhoneNumber = translate('phone_number_must_start_with', { x: '3, 5, 7, 8, or 9' });
    }

    // Captcha validation
    if (!validateCaptcha(formData.captcha.trim())) {
      valid = false;
      loadCaptchaEnginge(4); // 🔄 reload captcha
      setFormData((prev) => ({ ...prev, captcha: '' }));
      _errors.captcha = translate('captcha_not_match');
    }

    setFormError(_errors);
    return valid;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      // if (!isValid()) return;

      const isExists = await checkIfNumberExist(formData.userPhoneNumber);
      if (!isExists) throw new Error(translate('number_not_exists'));

      let fingerprint = localStorage.getItem('fingerprint');
      if (!fingerprint) {
        fingerprint = uuidv4();
        localStorage.setItem('fingerprint', fingerprint);
      }

      const response = await conObj.post('otp_request.json', {
        no_hp: formData.userPhoneNumber.replace(/[^0-9]/g, ''),
        fingerprint,
        purpose: 'forgotpassword',
      });
      enqueueSnackbar(translate('otp_sent_success'), { variant: 'success' });
      handleClose();
      // OTP REQUEST JSON ERROR
      dispatch(
        openReset({
          otpUuid: response.data?.data?.data?.otp_uuid,
          phoneNumber: formData.userPhoneNumber.replace(/[^0-9]/g, ''),
        })
      );
      // navigate('/auth/reset-password', {
      //   state: {
      //     otpUuid: response.data?.data?.data?.otp_uuid,
      //     phoneNumber: formData.userPhoneNumber.replace(/[^0-9]/g, ''),
      //   },
      // });
    } catch (err) {
      console.error(err);
      setError(getOtpErrorMessage(translate, err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MyDialog open={handleOpen} close={handleClose} maxSize="sm">
      <FormContainer onSubmit={onSubmit}>
        <Stack spacing={2}>
          <Typography variant="h3">{translate('forgot_password')}</Typography>

          {!!error && <Alert severity="error">{error}</Alert>}

          {/* Phone number */}
          <Box>
            <StyledLabel>{translate('phone_number')}</StyledLabel>
            <StyledInput
              name="userPhoneNumber"
              value={formData.userPhoneNumber}
              onChange={handleFormChange}
              fullWidth
              placeholder="e.g. 912345678"
            />
            {formError.userPhoneNumber && <ErrorText>{formError.userPhoneNumber}</ErrorText>}
          </Box>

          {/* Captcha */}
          <Box
            display={'flex'}
            width={'100%'}
            gap={1}
            mt={2}
            alignItems={'center'}
            sx={{
              '& canvas': {
                height: '45px !important',
                width: '100px !important',
                borderRadius: '8px',
                cursor: 'pointer',
                border: '1px solid #ccc',
                display: 'block',
              },
              '& #react-captcha-load-canvas-no-reload': {
                display: 'flex',
                alignItems: 'center',
                height: '45px',
              },
            }}
          >
            <LoadCanvasTemplateNoReload />
            <Box width={'100%'}>
              <StyledInput
                sx={{
                  height: '45px',
                  '& .MuiInputBase-root': { height: '45px' },
                }}
                fullWidth
                name="captcha"
                placeholder={translate('enter_text_on_the_left')}
                value={formData.captcha}
                onChange={handleFormChange}
              />
              {formError.captcha && <ErrorText>{formError.captcha}</ErrorText>}
            </Box>
          </Box>

          <Button
            type="submit"
            fullWidth
            disabled={isLoading}
            sx={{
              mt: 4,
              mb: 2,
              background: 'linear-gradient(to right, #c34029, #ff7214)',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '16px',
              borderRadius: '8px',
            }}
          >
            Confirm
          </Button>
        </Stack>
      </FormContainer>
    </MyDialog>
  );
}
